import { useMemo, useState } from "react";

interface Actions {
    show: () => void;
    hide: () => void;
    set: (value: boolean) => void;
    toggle: () => void;
    showAndHideAfterDelay: (delay: number) => void;
}

export const useVisibility = (initialValue = false): [boolean, Actions] => {
    const [visible, setVisible] = useState(initialValue);

    const actions = useMemo(() => {
        const toggle = () => setVisible((value) => !value);
        const set = (value: boolean) => setVisible(value);
        const show = () => setVisible(true);
        const hide = () => setVisible(false);
        const showAndHideAfterDelay = (delay: number) => {
            show();
            setTimeout(() => {
                hide();
            }, delay);
        };

        return {
            toggle,
            set,
            show,
            hide,
            showAndHideAfterDelay,
        };
    }, []);

    return [visible, actions];
};

// This is another alternative to useVisibility whereby we don't have to write
// long lines for the toggleMethods
// This:
// const [isDeleteModalOpen, { toggle: toggleDeleteModal, hide: hideDeleteModal }] = useVisibility(false);
// turns into:
// const deleteModal = useModal();
//      deleteModal.hide()
//      deleteModal.toggle();
//      deleteModal.isVisible
export const useModal = (initialValue = false) => {
    const [isVisible, { toggle, set, show, hide }] = useVisibility(initialValue);
    return { isVisible, toggle, set, show, hide };
};
